import { GenericToolApprovalContent } from "#components/ui/tool-approval/GenericToolApprovalContent.tsx";
import { resolveToolApprovalRenderer } from "#components/ui/tool-approval/renderers/index.ts";
import type { ToolApprovalCardProps } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import type { PendingApprovalCall } from "#types/ToolApproval.ts";
import { ErrorBoundary } from "react-error-boundary";

interface Props extends Omit<ToolApprovalCardProps, "args" | "display"> {
    call: Pick<PendingApprovalCall, "toolCallId" | "toolName" | "args" | "display">;
    isBatch: boolean;
    // Called when the tool's card throws and the generic content takes its place.
    onCardError?: () => void;
}

/**
 * One call's content: its tool's card when it has one, else the generic content. A card that throws or whose guard
 * rejects the call falls back to the generic content, so one bad card never takes down the prompt or the route.
 */
export function ToolApprovalCardBody({ call, isBatch, onCardError, ...cardProps }: Props) {
    const renderer = resolveToolApprovalRenderer(call);
    const generic = <GenericToolApprovalContent call={call} isBatch={isBatch} />;

    if (renderer === null) {
        return generic;
    }

    return (
        // The boundary holds its fallback until reset; key it on the call so a new prompt retries the card.
        <ErrorBoundary fallback={generic} resetKeys={[call.toolCallId]} onError={onCardError}>
            <renderer.Card {...cardProps} args={call.args} display={call.display ?? null} />
        </ErrorBoundary>
    );
}
